/**
 * iter703 — Upsell rules admin page.
 *
 * Lets the admin curate "when customer adds X, suggest Y / Z" pairings.
 * Drag rows to reorder priority, toggle off without deleting, link a
 * rule to a specific branch if needed.
 *
 * The rule format is intentionally simple — one trigger → many
 * suggestions — because that's how operators talk about it (e.g.
 * "Whenever someone orders the burger, push fries and a coke"). More
 * complex multi-trigger AND-rules can be layered on later without
 * breaking this schema.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  GripVertical, Plus, Trash2, Eye, EyeOff, Loader2, Info, Save,
  Sparkles, ChevronRight, X, Wand2, TrendingUp, RefreshCw, Check,
} from 'lucide-react';

import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';
import { resolveUploadUrl } from '@/lib/uploadUrl';
import { useBranchStore } from '@/stores/branchStore';

export default function UpsellsSettingsPage() {
  const { branches, fetchBranches } = useBranchStore();
  const [rules, setRules] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  // iter704 — Auto-learning panel state.
  const [autoCandidates, setAutoCandidates] = useState([]);
  const [autoMeta, setAutoMeta] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [adoptingId, setAdoptingId] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('upsells_dismissed_v1') || '[]')); }
    catch { return new Set(); }
  });

  useEffect(() => { fetchBranches?.(); }, [fetchBranches]);

  // ── Load ────────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    try {
      const [rulesRes, menuRes] = await Promise.all([
        api.get('/upsells/rules'),
        api.get('/cashier/menu'),
      ]);
      setRules(Array.isArray(rulesRes.data?.items) ? rulesRes.data.items : []);
      const items = Array.isArray(menuRes.data) ? menuRes.data : (menuRes.data?.items || []);
      setMenuItems(items);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load rules');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // ── iter704 — Auto-learning: load suggestions from order history ──
  const loadAuto = async () => {
    setAutoLoading(true);
    try {
      const { data } = await api.get('/upsells/suggested-rules', {
        params: { days: 365, min_support: 3, top_k: 20 },
      });
      setAutoCandidates(Array.isArray(data?.candidates) ? data.candidates : []);
      setAutoMeta({ scanned: data?.scanned_orders || 0, window: data?.window_days || 365 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not fetch suggestions');
    } finally {
      setAutoLoading(false);
    }
  };
  useEffect(() => { loadAuto(); }, []);

  const adoptCandidate = async (cand) => {
    const trig = cand.trigger_item?.id;
    const sugg = (cand.suggested_items || []).map((s) => s.id);
    if (!trig || sugg.length === 0) return;
    const key = `${trig}|${sugg.join(',')}`;
    setAdoptingId(key);
    try {
      await api.post('/upsells/adopt-suggested', {
        trigger_item_id: trig,
        suggested_item_ids: sugg,
        active: true,
      });
      toast.success(`Rule adopted: ${cand.trigger_item?.name} → ${cand.suggested_items?.map((s) => s.name).join(', ')}`);
      setAutoCandidates((prev) => prev.filter((c) => c.trigger_item?.id !== trig));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Adopt failed');
    } finally {
      setAdoptingId(null);
    }
  };

  const dismissCandidate = (cand) => {
    const id = cand.trigger_item?.id;
    if (!id) return;
    const next = new Set([...dismissed, id]);
    setDismissed(next);
    try { localStorage.setItem('upsells_dismissed_v1', JSON.stringify([...next])); } catch { /* ignored */ }
  };

  const visibleAutoCandidates = useMemo(
    () => autoCandidates.filter((c) => !dismissed.has(c.trigger_item?.id)),
    [autoCandidates, dismissed],
  );

  const itemsById = useMemo(() => {
    const m = {};
    for (const it of menuItems) m[it.id] = it;
    return m;
  }, [menuItems]);

  // ── Per-row updates ─────────────────────────────────────────────
  const updateRow = (id, patch) => {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch, _dirty: true } : r)));
  };

  const saveRow = async (rule) => {
    setSavingId(rule.id);
    try {
      const payload = {
        trigger_item_id: rule.trigger_item_id,
        suggested_item_ids: rule.suggested_item_ids,
        active: !!rule.active,
        branch_id: rule.branch_id || null,
      };
      await api.patch(`/upsells/rules/${rule.id}`, payload);
      toast.success('Rule saved');
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, _dirty: false } : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const removeRow = async (rule) => {
    if (!window.confirm('Delete this upsell rule?')) return;
    try {
      await api.delete(`/upsells/rules/${rule.id}`);
      toast.success('Rule deleted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // ── Drag-to-reorder ─────────────────────────────────────────────
  const onDragStart = (idx) => () => setDragIdx(idx);
  const onDragOver = (idx) => (e) => { e.preventDefault(); if (idx !== overIdx) setOverIdx(idx); };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };
  const onDrop = (idx) => async (e) => {
    e.preventDefault();
    if (dragIdx == null || dragIdx === idx) { onDragEnd(); return; }
    const next = [...rules];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setRules(next);
    onDragEnd();
    try { await api.post('/upsells/rules/reorder', { ids: next.map((r) => r.id) }); }
    catch (err) { toast.error('Reorder failed'); load(); }
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5" data-testid="upsells-settings-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sparkles size={22} className="text-primary" /> Upsell rules
            </h1>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              When a cashier adds a <strong>trigger</strong> item to the cart, the POS pops up the matching suggestions as 1-tap chips and the customer display shows a friendly &ldquo;Add fries · +5 SAR?&rdquo; card. Drag rows to set priority — higher rules suggest first.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 hidden sm:inline">{rules.filter((r) => r.active).length} active / {rules.length} total</span>
            <Button variant="ghost" size="sm" onClick={() => setShowHelp(true)} data-testid="upsells-help-btn">
              <Info size={14} className="mr-1" /> How it works
            </Button>
            <Button onClick={() => setShowAdd(true)} className="bg-primary hover:bg-primary/90 text-white" data-testid="upsells-add-btn">
              <Plus size={14} className="mr-1.5" /> Add rule
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-zinc-500 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            {/* iter704 — Auto-learning "Suggested for you" panel */}
            <AutoSuggestedPanel
              candidates={visibleAutoCandidates}
              meta={autoMeta}
              loading={autoLoading}
              adoptingId={adoptingId}
              onRefresh={loadAuto}
              onAdopt={adoptCandidate}
              onDismiss={dismissCandidate}
            />

            {rules.length === 0 ? (
          <Card>
            <CardContent className="p-10 text-center" data-testid="upsells-empty-state">
              <Sparkles size={32} className="mx-auto text-zinc-300 mb-3" />
              <p className="text-zinc-600 font-medium">No upsell rules yet</p>
              <p className="text-xs text-zinc-500 mt-1">Add one to start nudging customers toward higher-margin items.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="upsells-list">
            {rules.map((rule, idx) => (
              <UpsellRow
                key={rule.id}
                rule={rule}
                idx={idx}
                isDragOver={overIdx === idx && dragIdx !== idx}
                branches={branches}
                menuItems={menuItems}
                itemsById={itemsById}
                saving={savingId === rule.id}
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver(idx)}
                onDragEnd={onDragEnd}
                onDrop={onDrop(idx)}
                onChange={(patch) => updateRow(rule.id, patch)}
                onSave={() => saveRow(rule)}
                onDelete={() => removeRow(rule)}
              />
            ))}
          </div>
        )}
          </>
        )}

        {showAdd && (
          <AddRuleModal
            menuItems={menuItems}
            branches={branches}
            onClose={() => setShowAdd(false)}
            onCreated={async () => { setShowAdd(false); await load(); }}
          />
        )}

        {showHelp && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={() => setShowHelp(false)}>
            <div className="bg-white rounded-2xl max-w-md p-6 space-y-3 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="upsells-help-modal">
              <h2 className="font-bold text-lg flex items-center gap-2"><Info size={18} className="text-primary" /> How upsells work</h2>
              <ol className="list-decimal ml-5 text-sm space-y-1.5 leading-relaxed">
                <li>Pick a <strong>trigger</strong> menu item — the one that &ldquo;unlocks&rdquo; suggestions when it lands in the cart.</li>
                <li>Pick one or more <strong>suggested</strong> items — these appear as 1-tap chips above the cart on the cashier POS.</li>
                <li>The customer-facing display also gets a small overlay <em>&ldquo;Add fries · +5 SAR?&rdquo;</em> so the buyer can nod yes.</li>
                <li>Suggestions already in the cart are auto-hidden so you never push the same item twice.</li>
                <li>Tie a rule to a branch to limit it (e.g. show drive-thru-only combos at the Riyadh location).</li>
              </ol>
              <div className="flex justify-end pt-1">
                <Button size="sm" onClick={() => setShowHelp(false)} data-testid="upsells-help-close">Got it</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// ── Row ───────────────────────────────────────────────────────────────

function UpsellRow({
  rule, idx, isDragOver, branches, menuItems, itemsById, saving,
  onDragStart, onDragOver, onDragEnd, onDrop, onChange, onSave, onDelete,
}) {
  const trigger = itemsById[rule.trigger_item_id] || rule.trigger_item;
  const suggested = (rule.suggested_item_ids || []).map((id) => itemsById[id]).filter(Boolean);

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
      className={`flex items-stretch gap-3 bg-white rounded-xl border ${isDragOver ? 'border-primary ring-2 ring-primary/30' : 'border-zinc-200'} p-3 shadow-sm`}
      data-testid={`upsell-row-${idx}`}
    >
      <div className="flex items-center text-zinc-400 cursor-grab active:cursor-grabbing px-1">
        <GripVertical size={18} />
      </div>

      {/* Trigger */}
      <div className="flex-shrink-0 w-56">
        <Label className="text-[10px] uppercase tracking-wider text-zinc-500">When cashier adds</Label>
        <ItemPicker
          value={rule.trigger_item_id}
          menuItems={menuItems}
          onChange={(id) => onChange({ trigger_item_id: id })}
          testId={`upsell-trigger-${idx}`}
        />
      </div>

      <div className="flex items-center text-zinc-300">
        <ChevronRight size={18} />
      </div>

      {/* Suggested items chips */}
      <div className="flex-1 min-w-0">
        <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Suggest these</Label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {suggested.map((it) => (
            <span key={it.id} className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-900 rounded-full pl-2 pr-1 py-0.5 text-xs">
              {it.name}
              <button
                type="button"
                className="rounded-full p-0.5 hover:bg-amber-200"
                onClick={() => onChange({ suggested_item_ids: rule.suggested_item_ids.filter((i) => i !== it.id) })}
              ><X size={12} /></button>
            </span>
          ))}
          <ItemPicker
            value=""
            menuItems={menuItems}
            placeholder="+ add"
            compact
            excludeIds={[rule.trigger_item_id, ...(rule.suggested_item_ids || [])]}
            onChange={(id) => id && onChange({ suggested_item_ids: [...(rule.suggested_item_ids || []), id] })}
            testId={`upsell-add-suggested-${idx}`}
          />
        </div>
      </div>

      {/* Branch */}
      <div className="w-40 flex-shrink-0">
        <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Branch</Label>
        <Select
          value={rule.branch_id || 'all'}
          onValueChange={(v) => onChange({ branch_id: v === 'all' ? null : v })}
        >
          <SelectTrigger className="h-8 text-sm" data-testid={`upsell-branch-${idx}`}><SelectValue placeholder="All branches" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All branches</SelectItem>
            {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Actions */}
      <div className="flex flex-col items-center justify-center gap-1.5 ml-1">
        <button
          type="button"
          title={rule.active ? 'Active — click to pause' : 'Paused — click to activate'}
          onClick={() => onChange({ active: !rule.active })}
          className={`p-1.5 rounded-md ${rule.active ? 'text-emerald-600 hover:bg-emerald-50' : 'text-zinc-400 hover:bg-zinc-100'}`}
          data-testid={`upsell-active-${idx}`}
        >
          {rule.active ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={onSave}
          disabled={saving || !rule._dirty}
          data-testid={`upsell-save-${idx}`}
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} className="mr-1" />}
          Save
        </Button>
        <button
          type="button"
          onClick={onDelete}
          className="text-rose-500 hover:bg-rose-50 p-1.5 rounded-md"
          title="Delete"
          data-testid={`upsell-delete-${idx}`}
        >
          <Trash2 size={14} />
        </button>
      </div>

      {trigger?.image_url && (
        <img src={resolveUploadUrl(trigger.image_url)} alt="" className="w-10 h-10 rounded-md object-cover absolute right-2 top-2 opacity-30 pointer-events-none" />
      )}
    </div>
  );
}

// ── Item picker (searchable native select) ────────────────────────────
function ItemPicker({ value, menuItems, onChange, placeholder, compact, excludeIds = [], testId }) {
  const exclude = new Set(excludeIds.filter(Boolean));
  const usable = menuItems.filter((m) => !exclude.has(m.id));
  return (
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger className={compact ? 'h-7 w-28 text-xs px-2' : 'h-8 text-sm'} data-testid={testId}>
        <SelectValue placeholder={placeholder || 'Pick item'} />
      </SelectTrigger>
      <SelectContent>
        {usable.map((it) => (
          <SelectItem key={it.id} value={it.id}>
            {it.name} {typeof it.price === 'number' && <span className="text-zinc-400 text-xs ml-1">· {it.price.toFixed(2)}</span>}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Add rule modal ────────────────────────────────────────────────────
function AddRuleModal({ menuItems, branches, onClose, onCreated }) {
  const [triggerId, setTriggerId] = useState('');
  const [suggestedIds, setSuggestedIds] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!triggerId) return toast.error('Pick a trigger item');
    if (suggestedIds.length === 0) return toast.error('Pick at least one suggestion');
    setSaving(true);
    try {
      await api.post('/upsells/rules', {
        trigger_item_id: triggerId,
        suggested_item_ids: suggestedIds,
        branch_id: branchId || null,
        active: true,
      });
      toast.success('Rule created');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="upsell-add-modal">
        <h2 className="font-bold text-lg flex items-center gap-2"><Plus size={18} className="text-primary" /> New upsell rule</h2>

        <div>
          <Label className="text-xs">When cashier adds *</Label>
          <ItemPicker
            value={triggerId}
            menuItems={menuItems}
            onChange={(id) => { setTriggerId(id); setSuggestedIds((s) => s.filter((x) => x !== id)); }}
            testId="upsell-modal-trigger"
          />
        </div>

        <div>
          <Label className="text-xs">Suggest these (one or more) *</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {suggestedIds.map((sid) => {
              const it = menuItems.find((m) => m.id === sid);
              return (
                <span key={sid} className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-900 rounded-full pl-2 pr-1 py-0.5 text-xs">
                  {it?.name || sid}
                  <button type="button" className="rounded-full p-0.5 hover:bg-amber-200" onClick={() => setSuggestedIds((s) => s.filter((x) => x !== sid))}>
                    <X size={12} />
                  </button>
                </span>
              );
            })}
            <ItemPicker
              value=""
              menuItems={menuItems}
              placeholder="+ add"
              compact
              excludeIds={[triggerId, ...suggestedIds]}
              onChange={(id) => id && setSuggestedIds((s) => [...s, id])}
              testId="upsell-modal-add-suggested"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs">Branch</Label>
          <Select value={branchId || 'all'} onValueChange={(v) => setBranchId(v === 'all' ? null : v)}>
            <SelectTrigger className="h-9 text-sm" data-testid="upsell-modal-branch"><SelectValue placeholder="All branches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} data-testid="upsell-modal-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-primary hover:bg-primary/90 text-white" data-testid="upsell-modal-save">
            {saving && <Loader2 size={12} className="animate-spin mr-1" />} Create rule
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Auto-learning panel (iter704) ─────────────────────────────────────
// Mines the last 365 days of paid POS orders for frequently co-purchased
// pairs and proposes them as one-click adoptable upsell rules. The panel
// quietly hides itself when there are no candidates so it doesn't add
// visual noise to brand-new tenants.
function AutoSuggestedPanel({ candidates, meta, loading, adoptingId, onRefresh, onAdopt, onDismiss }) {
  if (loading && candidates.length === 0) {
    return (
      <div className="rounded-2xl border border-violet-200 bg-violet-50/50 p-4 flex items-center gap-2 text-violet-900 text-sm" data-testid="upsells-auto-loading">
        <Loader2 size={16} className="animate-spin" />
        Mining order history for upsell opportunities…
      </div>
    );
  }
  if (candidates.length === 0 && (!meta || meta.scanned === 0)) {
    return null;
  }

  return (
    <div className="rounded-2xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-4 sm:p-5" data-testid="upsells-auto-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-violet-700 font-bold flex items-center gap-1.5 mb-0.5">
            <Wand2 size={13} /> Suggested for you
          </p>
          <h2 className="font-bold text-lg text-zinc-900">Auto-learned from your order history</h2>
          {meta && (
            <p className="text-xs text-zinc-600 mt-0.5" data-testid="upsells-auto-meta">
              Scanned <strong>{meta.scanned}</strong> paid orders over the last <strong>{meta.window}</strong> days.
            </p>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading} data-testid="upsells-auto-refresh">
          {loading ? <Loader2 size={13} className="animate-spin mr-1" /> : <RefreshCw size={13} className="mr-1" />}
          Refresh
        </Button>
      </div>

      {candidates.length === 0 ? (
        <div className="text-sm text-zinc-600 bg-white rounded-xl p-4 text-center" data-testid="upsells-auto-empty">
          No new opportunities right now. Either the items in your menu aren&rsquo;t frequently bought together yet, or every co-purchase pattern is already covered by a rule.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="upsells-auto-list">
          {candidates.map((c) => {
            const key = `${c.trigger_item?.id}|${(c.suggested_items || []).map((s) => s.id).join(',')}`;
            const adopting = adoptingId === key;
            return (
              <div key={key} className="bg-white border border-violet-200 rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-2" data-testid={`upsell-auto-card-${c.trigger_item?.id}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500">When customer adds</p>
                    <p className="font-bold text-zinc-900 text-sm truncate" data-testid={`upsell-auto-trigger-${c.trigger_item?.id}`}>{c.trigger_item?.name}</p>
                  </div>
                  <span className="text-[10px] inline-flex items-center gap-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full px-1.5 py-0.5 font-semibold flex-shrink-0">
                    <TrendingUp size={10} /> {Math.round((c.confidence || 0) * 100)}%
                  </span>
                </div>
                <div className="border-t border-zinc-100 pt-2">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Suggest</p>
                  <div className="flex flex-wrap gap-1">
                    {(c.suggested_items || []).map((s) => (
                      <span key={s.id} className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-900 rounded-full px-2 py-0.5 text-[11px]">
                        {s.name}
                        {typeof s.price === 'number' && <span className="text-amber-700/80">· {s.price.toFixed(0)}</span>}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-baseline justify-between text-[11px] text-zinc-600 mt-1">
                  <span>Co-purchased <strong>{c.co_purchased_count}×</strong> in <strong>{c.support}+</strong> orders</span>
                  {typeof c.est_revenue === 'number' && <span className="font-semibold text-emerald-700">≈ {c.est_revenue.toFixed(1)} avg upsell value</span>}
                </div>
                <div className="flex justify-end gap-1.5 pt-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => onDismiss(c)}
                    disabled={adopting}
                    data-testid={`upsell-auto-dismiss-${c.trigger_item?.id}`}
                  >
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white"
                    onClick={() => onAdopt(c)}
                    disabled={adopting}
                    data-testid={`upsell-auto-adopt-${c.trigger_item?.id}`}
                  >
                    {adopting ? <Loader2 size={11} className="animate-spin mr-1" /> : <Check size={11} className="mr-1" />}
                    Adopt
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

